import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import { type Stage, ACTOR_COLOR, ACTOR_LABEL } from "./diagramData";
import { ServiceChip } from "../ServiceChip";

export type StageNodeData = Node<{ stage: Stage }, "stage">;

/** A single instrument-dial stage node. Number badge (actor-accent ring),
 *  etched stage name, actor + action, and the live service chips. Hover lifts
 *  and glows; connected edges highlight via the group hover in FilmLabDiagram. */
export function StageNode({ data }: NodeProps<StageNodeData>) {
  const { stage } = data;
  const accent = ACTOR_COLOR[stage.actor];

  return (
    <div
      className="panel panel-hover group relative w-[250px] p-4 transition-transform hover:-translate-y-0.5 hover:!shadow-[var(--panel-shadow),var(--glow)]"
      style={{ borderTopColor: accent, borderTopWidth: 2 }}
    >
      {/* hidden anchors — floating edges compute geometry from the node border */}
      <Handle type="target" position={Position.Top} className="!h-1 !w-1 !min-w-0 !border-0 !bg-transparent !opacity-0" />
      <Handle type="source" position={Position.Bottom} className="!h-1 !w-1 !min-w-0 !border-0 !bg-transparent !opacity-0" />

      <div className="flex items-center gap-2.5">
        <span
          className="flex h-8 w-8 flex-none items-center justify-center rounded-full border font-mono text-lg leading-none"
          style={{ borderColor: accent, color: accent }}
        >
          {stage.n}
        </span>
        <div>
          <div className="etch-label etch !text-[13px] !tracking-[0.1em] !text-text">
            {stage.name}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.1em]" style={{ color: accent }}>
            {ACTOR_LABEL[stage.actor]}
          </div>
        </div>
      </div>

      <p className="mt-2.5 text-[13px] leading-[1.5] text-text-dim">{stage.action}</p>

      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {stage.services.map((s) => (
          <ServiceChip key={s} app={s} className="!py-1 !text-[11px]" />
        ))}
      </div>
    </div>
  );
}
